import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, ArrowDownLeft, ArrowUpRight, TrendingUp, Landmark, Loader2, Link2, ExternalLink } from "lucide-react";

/**
 * 4 Xero KPI cards: invoices owed to you · bills to pay · net profit YTD ·
 * bank accounts. Cached 15min server-side. Compact mode renders for the
 * Overview embed; full mode for the dedicated /app/financials page.
 *
 * Renders an empty/CTA state if Xero isn't connected so the section is
 * still discoverable.
 */
export function XeroFinancialSnapshot({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/xero/financials/snapshot${force ? "?force=1" : ""}`);
      setData(data);
    } catch {
      setData({ connected: false });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  if (loading) {
    return (
      <Section compact={compact}>
        <div className="hard-card p-6 flex items-center gap-2.5 text-zinc-600 dark:text-zinc-400 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Xero snapshot…
        </div>
      </Section>
    );
  }

  if (!data?.connected) {
    return (
      <Section compact={compact} fetchedAt={null}>
        <Link to="/app/integrations/xero" className="block">
          <div className="hard-card p-5 hover:border-[#13B5EA] transition-colors group" data-testid="xero-snapshot-cta">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#13B5EA] text-white flex items-center justify-center font-bold text-base shrink-0">X</div>
              <div className="flex-1">
                <div className="font-semibold text-[14px]">Connect Xero to see your live financials here</div>
                <div className="text-[12px] text-zinc-600 dark:text-zinc-400">
                  Invoices owed · Bills to pay · Net profit YTD · Bank accounts. 30 seconds to connect.
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-[#13B5EA]" />
            </div>
          </div>
        </Link>
      </Section>
    );
  }

  const f = data;
  const np = f.net_profit_ytd;
  const npFromCache = f.from_cache;

  return (
    <Section
      compact={compact}
      fetchedAt={f.fetched_at}
      tenant={f.tenant_name}
      onRefresh={() => load(true)}
      refreshing={refreshing}
    >
      <div className={`grid gap-3 ${compact ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
        <Card
          tone="ar"
          icon={ArrowDownLeft}
          label="Invoices owed to you"
          big={fmt(f.invoices_owed?.amount)}
          subA={`${f.invoices_owed?.count || 0} awaiting payment`}
          subB={f.invoices_owed?.overdue_count > 0
            ? `${f.invoices_owed.overdue_count} overdue · ${fmt(f.invoices_owed.overdue_amount)}`
            : "No overdue"}
          testid="xero-kpi-ar"
        />
        <Card
          tone="ap"
          icon={ArrowUpRight}
          label="Bills to pay"
          big={fmt(f.bills_to_pay?.amount)}
          subA={`${f.bills_to_pay?.count || 0} awaiting payment`}
          subB={f.bills_to_pay?.overdue_count > 0
            ? `${f.bills_to_pay.overdue_count} overdue · ${fmt(f.bills_to_pay.overdue_amount)}`
            : "No overdue"}
          testid="xero-kpi-ap"
        />
        <Card
          tone="profit"
          icon={TrendingUp}
          label="Net profit YTD"
          big={np == null ? "—" : fmt(np)}
          subA={f.ytd_period?.from && f.ytd_period?.to
            ? `${(f.ytd_period.from || "").slice(5)} → ${(f.ytd_period.to || "").slice(5)}`
            : "Year to date"}
          subB={np == null ? "Couldn't read P&L report" : null}
          testid="xero-kpi-profit"
        />
        <Card
          tone="bank"
          icon={Landmark}
          label="Bank accounts"
          big={f.bank?.count || 0}
          subA={(f.bank?.accounts || []).slice(0, 2).map((a) => a.name).join(" · ") || "No active bank accounts"}
          subB={(f.bank?.accounts?.length || 0) > 2 ? `+${f.bank.accounts.length - 2} more` : null}
          testid="xero-kpi-bank"
        />
      </div>
      {npFromCache && !compact && (
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Cached snapshot · Tap Refresh to pull live from Xero
        </div>
      )}
    </Section>
  );
}

function Section({ compact, fetchedAt, tenant, onRefresh, refreshing, children }) {
  return (
    <section className={compact ? "mb-6" : "mb-8"} data-testid="xero-financial-snapshot">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="label-tag !text-[#FF5F15]">// Financials from Xero</span>
          {tenant && <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{tenant}</span>}
        </div>
        {onRefresh && (
          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
            {fetchedAt && <span className="font-mono">refreshed {(fetchedAt || "").slice(11, 16)}</span>}
            <Button
              data-testid="xero-snapshot-refresh"
              size="sm"
              variant="outline"
              onClick={onRefresh}
              disabled={refreshing}
              className="rounded-sm h-7 text-[11px]"
            >
              {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Refresh
            </Button>
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function Card({ icon: Icon, tone, label, big, subA, subB, testid }) {
  const tones = {
    ar:     "border-l-emerald-500",
    ap:     "border-l-amber-500",
    profit: "border-l-[#FF5F15]",
    bank:   "border-l-[#13B5EA]",
  };
  return (
    <div className={`hard-card p-4 border-l-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="font-display font-black text-3xl leading-none tracking-tight tabular-nums text-zinc-900 dark:text-white">{big}</div>
      {subA && <div className="text-[11.5px] text-zinc-700 dark:text-zinc-300 mt-1.5 font-medium">{subA}</div>}
      {subB && <div className="text-[11px] text-zinc-500 dark:text-zinc-500 mt-0.5">{subB}</div>}
    </div>
  );
}

function fmt(n) {
  if (n == null || isNaN(n)) return "—";
  // Plain Xero-style number with thousands separator + 2dp
  return Number(n).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
